import { getBytes, getMetadata, ref } from 'firebase/storage';
import type { FrameRecord } from '@erectus/shared';
import { firebase } from '@/lib/firebase.js';
import { BIN_MAGIC, dequantize, gunzip, unpackFrames } from '@/session/db.js';
import { metricsFromLandmarks } from '@/metrics/index.js';
import { detectSide } from '@/pose/qualityGate.js';
import { levelLandmarks } from '@/pose/level.js';
import { PORTRAIT_IDEAL } from '@/lib/camera.js';

/**
 * Replay frames from the user's own shared keypoint file
 * (`sessions/{uid}/{sessionId}.bin`, uploaded only with the data opt-in —
 * CLAUDE.md principle 3). This is what lets a shared session replay on another
 * device; everyone else replays from the frames on the phone that recorded.
 *
 * The file holds time, state and landmarks. The neck and trunk angles are
 * worked out again with the same metric functions and the session's phone-roll
 * correction; only the live 1 s median smoothing is missing, so a label can
 * differ from the recorded value by a degree or so.
 */

const ASPECT = PORTRAIT_IDEAL.width / PORTRAIT_IDEAL.height;

function pathFor(uid: string, sessionId: string): string {
  return `sessions/${uid}/${sessionId}.bin`;
}

/** Whether a shared file exists for this session. Never throws. */
export async function hasSharedFrames(uid: string, sessionId: string): Promise<boolean> {
  try {
    await getMetadata(ref(firebase().storage, pathFor(uid, sessionId)));
    return true;
  } catch {
    return false;
  }
}

/** One download per session per app run: the report strip, snapshots and replay all read the same file. */
const loaded = new Map<string, Promise<FrameRecord[]>>();

export function loadSharedFrames(
  uid: string,
  sessionId: string,
  phoneRollDeg: number | null | undefined,
): Promise<FrameRecord[]> {
  const key = `${uid}/${sessionId}/${phoneRollDeg ?? 0}`;
  let pending = loaded.get(key);
  if (!pending) {
    pending = fetchSharedFrames(uid, sessionId, phoneRollDeg);
    pending.catch(() => loaded.delete(key)); // a failed fetch can be retried
    loaded.set(key, pending);
  }
  return pending;
}

async function fetchSharedFrames(
  uid: string,
  sessionId: string,
  phoneRollDeg: number | null | undefined,
): Promise<FrameRecord[]> {
  let buffer = await getBytes(ref(firebase().storage, pathFor(uid, sessionId)));
  // Served with Content-Encoding: gzip the browser has already inflated it; a
  // copy uploaded without CompressionStream was never compressed. Check the bytes.
  if (buffer.byteLength >= 4 && new DataView(buffer).getUint32(0) !== BIN_MAGIC) buffer = await gunzip(buffer);
  return framesWithAngles(unpackFrames(buffer).frames, phoneRollDeg ?? 0);
}

/** `aspect` is the picture's width / height: sessions are recorded portrait 3:4 (CLAUDE.md capture invariant 2). */
export function framesWithAngles(
  rows: ReadonlyArray<Pick<FrameRecord, 't' | 'state' | 'lm'>>,
  rollDeg: number,
  aspect = ASPECT,
): FrameRecord[] {
  return rows.map((row) => {
    if (row.state === 'invalid') return { ...row, cva: null, cha: null, trunk: null, prot: null, hip: null, knee: null };
    const landmarks = levelLandmarks(dequantize(row.lm), aspect, rollDeg);
    const m = metricsFromLandmarks(landmarks, detectSide(landmarks), aspect);
    return { ...row, cva: m.cva, cha: m.cha, trunk: m.trunkInclination, prot: m.shoulderProtraction, hip: m.hipAngle, knee: m.kneeAngle };
  });
}
